import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as catalogService from '../services/portalCatalogService';

interface FavoriteButtonProps {
	itemId: number;
	isFavorite: boolean;
	/** Called after the favorite state changes so the parent can update its data */
	onToggled?: () => void;
	/** Size variant */
	size?: 'sm' | 'md';
}

/**
 * Star-shaped favorite toggle button.
 * Grey outline star when not favorited, filled gold star when favorited.
 * Requires auth — opens login modal if not authenticated.
 */
export function FavoriteButton({
	itemId,
	isFavorite,
	onToggled,
	size = 'sm',
}: FavoriteButtonProps): JSX.Element {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const requireAuth = useAuthGate();
	const [isLoading, setIsLoading] = useState(false);

	const handleToggle = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (!requireAuth()) return;
			if (token == null) return;

			setIsLoading(true);
			try {
				if (isFavorite) {
					await catalogService.removeFavorite(token, itemId);
				} else {
					await catalogService.addFavorite(token, itemId);
				}
				onToggled?.();
			} finally {
				setIsLoading(false);
			}
		},
		[requireAuth, token, itemId, isFavorite, onToggled],
	);

	const iconSize = size === 'md' ? 'text-lg' : 'text-sm';

	return (
		<button
			type="button"
			onClick={handleToggle}
			disabled={isLoading}
			className={`flex items-center justify-center rounded-full transition-colors disabled:opacity-50 ${
				size === 'md' ? 'w-10 h-10' : 'w-8 h-8'
			} ${
				isFavorite
					? 'text-warning hover:text-warning'
					: 'text-faint hover:text-warning'
			}`}
			aria-label={
				isFavorite ? t('Remove from favorites') : t('Add to favorites')
			}
		>
			<FontAwesomeIcon
				icon={isFavorite ? faStarSolid : faStarRegular}
				className={iconSize}
			/>
		</button>
	);
}
